import { logDebug } from '@ptek/client-logger';
import { useCallback, useEffect, useState } from 'react';
import { useWidgetEvents } from './useWidgetEvents';
/**
 * Hook for managing widget expand/collapse state from within React apps
 *
 * @returns Object containing expand/collapse state and control functions
 */
export function useWidgetExpandCollapse() {
    const [isExpanded, setIsExpanded] = useState(false); // Default to collapsed to match startCollapsed: true
    const [isReady, setIsReady] = useState(false);
    const { emitToHost, onHostEvent } = useWidgetEvents();
    // Listen for widget ready event and initial state
    useEffect(() => {
        const handleReady = () => {
            setIsReady(true);
            // Request the current expand/collapse state from the widget shell
            emitToHost('requestExpandCollapseState');
        };
        const cleanupReady = onHostEvent('ready', handleReady);
        return () => {
            cleanupReady();
        };
    }, [emitToHost, onHostEvent]);
    // Listen for expand/collapse state changes from the widget shell
    useEffect(() => {
        const handleExpanded = () => {
            setIsExpanded(true);
        };
        const handleCollapsed = () => {
            setIsExpanded(false);
        };
        const handleExpandCollapseState = (data) => {
            setIsExpanded(data.isExpanded);
        };
        // Listen to host events for expand/collapse state changes
        const cleanupExpanded = onHostEvent('expanded', handleExpanded);
        const cleanupCollapsed = onHostEvent('collapsed', handleCollapsed);
        const cleanupState = onHostEvent('expandCollapseState', handleExpandCollapseState);
        return () => {
            cleanupExpanded();
            cleanupCollapsed();
            cleanupState();
        };
    }, [onHostEvent]);
    // Request expand from the widget shell
    const expand = useCallback(() => {
        emitToHost('requestExpand');
    }, [emitToHost]);
    // Request collapse from the widget shell
    const collapse = useCallback(() => {
        logDebug('useWidgetExpandCollapse', 'Requesting collapse');
        emitToHost('requestCollapse');
    }, [emitToHost]);
    // Toggle expand/collapse state
    const toggleExpandCollapse = useCallback(() => {
        emitToHost('requestToggleExpandCollapse');
    }, [emitToHost]);
    return {
        isExpanded,
        isReady,
        expand,
        collapse,
        toggleExpandCollapse,
    };
}
//# sourceMappingURL=useWidgetExpandCollapse.js.map